import { Badge, Checkbox, FooterCell, Stack, Tooltip, Typography } from '@ui-components/neo.tax';
import { useMemo, ReactElement } from 'react';
import { CellProps, Column, FooterProps, type UseRowSelectInstanceProps } from 'react-table';

import { useAscContext } from '@neo.tax/asc/context';
import { useIsCapitalizedProjectClustering } from '@neo.tax/asc/hooks';
import { AscProject } from '@neo.tax/asc/hooks/useAscProjects';
import { AscProjectsSummary } from '@neo.tax/asc/hooks/useAscProjectsSummary';
import {
  calculateAverage,
  getPath,
  Path,
  useCurrentCompanyId,
  TableHeader,
  ProjectLeftBar,
  ProjectName,
  useHasSkippedConnectingTicketing,
  TABLE_CELL_PLACEHOLDER,
  HIGHLIGHT_VARIANT,
} from '@neo.tax/shared';

import { getCapitalizationDateString, PROJECT_DATES_DESCRIPTION } from '../asc-projects-layout.utils';
import { AscProjectCapitalizedAIGuidanceCell } from '../AscProjectCapitalizedAIGuidanceCell';
import { AscProjectCapitalizedCell } from '../AscProjectCapitalizedCell';
import { AscProjectCapitalizationEndDateCell } from '../AscProjectCapitalizedEndDateCell';
import { AscProjectCapitalizationStartDateCell } from '../AscProjectCapitalizedStartDateCell';
import { CapitalizableProjectClusteringBadgeCell } from '../CapitalizableProjectClusteringBadgeCell';

interface UseAscProjectsTableColumnsProps<T extends Record<string, any>> {
  clickedRowId?: string;
  footerData?: AscProjectsSummary | null;
  onSelectAllRows: (checked: boolean) => void;
  onSelectRow: (checked: boolean, item: T) => void;
  rowCount: number;
  selectedRowIds: string[];
  visibleColumns: string[];
}

export const useAscProjectsTableColumns = ({
  clickedRowId,
  footerData,
  onSelectAllRows,
  onSelectRow,
  rowCount,
  selectedRowIds,
  visibleColumns,
}: UseAscProjectsTableColumnsProps<AscProject>): Column<AscProject>[] => {
  const companyId = useCurrentCompanyId();
  const { fiscalYearId, periodName } = useAscContext();
  const { hasSkippedConnectingTicketing } = useHasSkippedConnectingTicketing();
  const isCapitalizedProjectClustering = useIsCapitalizedProjectClustering();

  const columns = useMemo<Column<AscProject>[]>(() => {
    // react-table passes UseRowSelectInstanceProps through at runtime but the Cell renderer
    // is typed as Renderer<CellProps<D, V>>. Casting inside the Cell body keeps the column
    // structurally assignable to Column<AscProject> while preserving access to toggleRowSelected.
    const handleSelectRow =
      (cell: CellProps<AscProject, AscProject> & UseRowSelectInstanceProps<AscProject>) =>
      (checked: boolean): void => {
        cell.toggleRowSelected(cell.value.id, checked);
        onSelectRow(checked, cell.value);
      };

    const baseColumns: Column<AscProject>[] = [
      {
        id: 'name',
        accessor: (datum: AscProject): AscProject => datum,
        width: 'minmax(360px, 420px)',
        Header: () => (
          <Stack direction="row">
            <Checkbox
              id="projects"
              name="projects"
              checked={selectedRowIds.length > 0 && selectedRowIds.length === rowCount}
              onChange={onSelectAllRows}
            />
            <TableHeader>Projects</TableHeader>
          </Stack>
        ),
        Cell: (cell: CellProps<AscProject, AscProject> & UseRowSelectInstanceProps<AscProject>): ReactElement => (
          <>
            <ProjectLeftBar $variant={HIGHLIGHT_VARIANT} $isClicked={clickedRowId === cell.value.id} />
            <Checkbox
              id={cell.value.id}
              name={cell.value.name}
              checked={selectedRowIds?.includes(cell.value.id)}
              onChange={handleSelectRow(cell)}
            />
            <ProjectName
              $variant={HIGHLIGHT_VARIANT}
              title={cell.value.name}
              to={getPath({
                path: Path.ASC_PROJECTS,
                companyId,
                fiscalYearId,
                periodName,
                ascProjectId: cell.value.id,
              })}
            >
              {cell.value.name}
            </ProjectName>
          </>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.count?.name ?? '0';
          return <FooterCell preText="COUNT" text={value} />;
        },
      },
    ];

    const dateColumns: Column<AscProject>[] = [
      {
        id: 'capitalizationStartDate',
        width: 180,
        accessor: (datum: AscProject): Date | null => datum.capitalizationStartDate,
        Header: () => <TableHeader>Capitalization Start</TableHeader>,
        Cell: ({ row, value }: CellProps<AscProject, Date | null>): ReactElement => (
          <AscProjectCapitalizationStartDateCell
            value={value}
            missingValueTooltip="Missing capitalization start date. Set manually in the project drawer"
            isCapitalized={row.original.isCapitalized}
          />
        ),
      },
      {
        id: 'capitalizationEndDate',
        width: 180,
        accessor: (datum: AscProject): Date | null => datum.capitalizationEndDate,
        Header: () => <TableHeader>Capitalization End</TableHeader>,
        Cell: ({ row, value }: CellProps<AscProject, Date | null>): ReactElement => {
          const predictedDifferenceTooltip = row.original.predictedCapitalizationEndDate
            ? `Capitalization end date differs from prediction: ${getCapitalizationDateString(
                row.original.predictedCapitalizationEndDate,
              )}. Set manually in the project drawer`
            : // should never display if there's no predicted end date
              '';

          return (
            <AscProjectCapitalizationEndDateCell
              value={value}
              extendedFrom={row.original.capitalizationEndDateExtendedFrom}
              missingValueTooltip="Missing capitalization end date. Set manually in the project drawer"
              isCapitalized={row.original.isCapitalized}
              predictedValue={row.original.predictedCapitalizationEndDate}
              predictedReason={row.original.predictedCapitalizationEndDateReason}
              predictedDifferenceTooltip={predictedDifferenceTooltip}
            />
          );
        },
      },
    ];

    let allColumns: Column<AscProject>[];

    if (hasSkippedConnectingTicketing) {
      allColumns = [...baseColumns, ...dateColumns];
    } else {
      allColumns = [
        ...baseColumns,
        {
          id: 'productName',
          width: 180,
          Header: (): ReactElement => <TableHeader>Product</TableHeader>,
          accessor: (datum: AscProject): AscProject['product'] => datum.product,
          Cell: ({ value }: CellProps<AscProject, AscProject['product']>): ReactElement =>
            value ? (
              <Badge colorVariant="yellow" dot={false} maxWidth={150}>
                {value.name}
              </Badge>
            ) : (
              <></>
            ),
        },
        {
          id: 'teams',
          width: 150,
          Header: (): ReactElement => <TableHeader>Teams</TableHeader>,
          accessor: (datum: AscProject): string[] => datum.workstreamNames ?? [],
          Cell: ({ value }: CellProps<AscProject, string[]>): ReactElement => (
            <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value.join(', ')}>
              {value.join(', ')}
            </Typography>
          ),
        },
        {
          id: 'tickets',
          width: 130,
          Header: (): ReactElement => <TableHeader textAlign="right">Tickets</TableHeader>,
          accessor: (datum: AscProject): number => datum.ticketCount ?? 0,
          Cell: ({ value }: CellProps<AscProject, number>): ReactElement => (
            <Stack fullWidth justifyContent="end">
              <Typography color="inherit" textOverflow="truncate" fontSize="inherit">
                {value}
              </Typography>
            </Stack>
          ),
          Footer: ({ rows }: FooterProps<AscProject>): ReactElement => {
            const average = calculateAverage(rows, (row) => row.original?.ticketCount ?? 0);
            return <FooterCell alignText="end" preText="AVG" text={`${Math.round(average)}`} />;
          },
        },
        {
          id: 'owner',
          width: 150,
          Header: (): ReactElement => <TableHeader textAlign="left">Owner</TableHeader>,
          accessor: (datum: AscProject): string => datum.projectOwner?.fullName ?? '',
          Cell: ({ value }: CellProps<AscProject, string>): ReactElement => (
            <Typography color="inherit" textOverflow="truncate" fontSize="inherit">
              {value || TABLE_CELL_PLACEHOLDER}
            </Typography>
          ),
        },
        {
          id: 'startDate',
          width: 150,
          Header: (): ReactElement => <TableHeader title={PROJECT_DATES_DESCRIPTION}>Start Date</TableHeader>,
          accessor: (datum: AscProject): Date | null => datum.startDate,
          Cell: ({ value }: CellProps<AscProject, Date | null>): ReactElement => (
            <Typography color="inherit" textOverflow="truncate" fontSize="inherit">
              {value ? getCapitalizationDateString(value) : TABLE_CELL_PLACEHOLDER}
            </Typography>
          ),
        },
        {
          id: 'endDate',
          width: 150,
          Header: (): ReactElement => <TableHeader title={PROJECT_DATES_DESCRIPTION}>End Date</TableHeader>,
          accessor: (datum: AscProject): Date | null => datum.endDate,
          Cell: ({ value }: CellProps<AscProject, Date | null>): ReactElement => (
            <Typography color="inherit" textOverflow="truncate" fontSize="inherit">
              {value ? getCapitalizationDateString(value) : TABLE_CELL_PLACEHOLDER}
            </Typography>
          ),
        },
        {
          id: 'aiGuidance',
          width: 180,
          Header: (): ReactElement => <TableHeader>AI Guidance</TableHeader>,
          accessor: (datum: AscProject): AscProject['predictedCapitalizationStatus'] =>
            datum.predictedCapitalizationStatus,
          Cell: ({ row }: CellProps<AscProject>): ReactElement => (
            <AscProjectCapitalizedAIGuidanceCell ascProject={row.original} />
          ),
        },
        ...dateColumns,
        {
          id: 'capitalized',
          width: 180,
          Header: (): ReactElement => <TableHeader>Capitalized</TableHeader>,
          accessor: (datum: AscProject): AscProject['isCapitalized'] => datum.isCapitalized,
          Cell: ({ row }: CellProps<AscProject>): ReactElement => (
            <AscProjectCapitalizedCell ascProject={row.original} fiscalYearId={fiscalYearId} periodName={periodName} />
          ),
        },
        ...(isCapitalizedProjectClustering
          ? [
              {
                id: 'capitalizableProjectClustering',
                width: 160,
                Header: (): ReactElement => (
                  <Tooltip text="Match capitalizable tickets to this project on next sync.">
                    <TableHeader>Next Sync Matching</TableHeader>
                  </Tooltip>
                ),
                accessor: (datum: AscProject): AscProject => datum,
                Cell: ({ value }: CellProps<AscProject, AscProject>): ReactElement => (
                  <CapitalizableProjectClusteringBadgeCell project={value} />
                ),
              } satisfies Column<AscProject>,
              {
                id: 'matchingTeams',
                width: 150,
                Header: (): ReactElement => (
                  <Tooltip text="Teams supplied for matching. Falls back to ticket workstreams when empty.">
                    <TableHeader>Matching Teams</TableHeader>
                  </Tooltip>
                ),
                accessor: (datum: AscProject): string[] => datum.customFieldTeams ?? [],
                Cell: ({ value }: CellProps<AscProject, string[]>): ReactElement => (
                  <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value.join(', ')}>
                    {value.join(', ')}
                  </Typography>
                ),
              } satisfies Column<AscProject>,
            ]
          : []),
      ];
    }

    // Simple filtering - just check if the column ID is in the visible columns
    return allColumns.filter((column) => column.id && visibleColumns.includes(column.id));
  }, [
    hasSkippedConnectingTicketing,
    onSelectRow,
    selectedRowIds,
    rowCount,
    onSelectAllRows,
    clickedRowId,
    companyId,
    visibleColumns,
    footerData,
    fiscalYearId,
    periodName,
    isCapitalizedProjectClustering,
  ]);

  return columns;
};
